/**
 * QUIPWA — ErrorFallback
 *
 * UI component shown when an error is caught by ErrorBoundary.
 * Displays error message, suggestions, and retry button.
 */

import { useEffect, useState } from 'react'
import type { ErrorInfo } from 'react'
import type { JSX } from 'react'

interface ErrorFallbackProps {
  error: Error
  errorInfo: ErrorInfo | null
  onRetry: () => void
  level?: 'critical' | 'component' | 'section' | undefined
}

import { useI18n } from '../../lib/i18n/i18n-store'

export function ErrorFallback({
  error,
  errorInfo,
  onRetry,
  level = 'component',
}: ErrorFallbackProps): JSX.Element {
  const { t } = useI18n()
  const isCritical = level === 'critical'
  const title = isCritical ? t('error.critical') : t('error.component')
  const backgroundColor = isCritical ? '#2d1b1b' : '#1a1a1a'
  const borderColor = isCritical ? '#ff4444' : '#ff8844'
  const [errorId, setErrorId] = useState(0)

  useEffect(() => {
    setErrorId(Date.now()) // eslint-disable-line react-hooks/set-state-in-effect
  }, [error])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: isCritical ? '100vh' : '300px',
        padding: '40px 20px',
        backgroundColor,
        borderRadius: isCritical ? '0' : '8px',
        border: isCritical ? 'none' : `2px solid ${borderColor}`,
        fontFamily: 'var(--font-mono, monospace)',
        color: '#ccc',
      }}
    >
      {/* Error Icon */}
      <div
        style={{
          fontSize: '48px',
          marginBottom: '20px',
          opacity: 0.8,
        }}
      >
        ⚠️
      </div>

      {/* Title */}
      <h2
        style={{
          fontSize: '20px',
          fontWeight: 600,
          marginBottom: '12px',
          color: borderColor,
        }}
      >
        {title}
      </h2>

      {/* Error Message */}
      <p
        style={{
          fontSize: '14px',
          marginBottom: '24px',
          textAlign: 'center',
          maxWidth: '500px',
          lineHeight: 1.6,
        }}
      >
        {error.message || 'Se produjo un error desconocido.'}
      </p>

      {/* Stack Trace (Development Only) */}
      {process.env.NODE_ENV === 'development' && errorInfo && (
        <details
          style={{
            marginBottom: '24px',
            fontSize: '11px',
            textAlign: 'left',
            overflowX: 'auto',
            backgroundColor: 'rgba(0,0,0,0.5)',
            padding: '12px',
            borderRadius: '4px',
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <summary style={{ cursor: 'pointer', color: '#aaa', marginBottom: '8px' }}>
            Detalles técnicos
          </summary>
          <pre
            style={{
              margin: 0,
              fontSize: '10px',
              color: '#999',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {errorInfo.componentStack}
          </pre>
        </details>
      )}

      {/* Suggestions */}
      <div
        style={{
          fontSize: '13px',
          marginBottom: '24px',
          color: '#999',
          textAlign: 'left',
        }}
      >
        <p style={{ marginBottom: '8px', fontWeight: 600, color: '#aaa' }}>
          Qué puedes hacer:
        </p>
        <ul
          style={{
            marginLeft: '20px',
            lineHeight: 1.8,
            listStyle: 'disc',
          }}
        >
          <li>{t('error.retryHint')}</li>
          <li>{t('error.refreshHint')}</li>
          <li>Limpia el almacenamiento local: Configuración → Limpiar datos</li>
          <li>{t('error.supportHint')}</li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={onRetry}
          style={{
            padding: '10px 20px',
            backgroundColor: borderColor,
            color: '#000',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'opacity 0.2s',
          }}
          onMouseOver={(e) => {
            (e.target as HTMLElement).style.opacity = '0.8'
          }}
          onMouseOut={(e) => {
            (e.target as HTMLElement).style.opacity = '1'
          }}
        >
          🔄 Reintentar
        </button>

        <a
          href="/"
          style={{
            padding: '10px 20px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#ccc',
            border: '1px solid #555',
            borderRadius: '6px',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px',
            textDecoration: 'none',
            transition: 'opacity 0.2s',
          }}
          onMouseOver={(e) => {
            (e.currentTarget as HTMLElement).style.opacity = '0.8'
          }}
          onMouseOut={(e) => {
            (e.currentTarget as HTMLElement).style.opacity = '1'
          }}
        >
          🏠 Ir al inicio
        </a>
      </div>

      {/* Error Reference */}
      <div
        style={{
          marginTop: '32px',
          fontSize: '12px',
          color: '#666',
          textAlign: 'center',
        }}
      >
        Error ID: {errorId}
        {error.message && ` — ${error.message.substring(0, 30)}...`}
      </div>
    </div>
  )
}
